import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, CheckCircle } from "@phosphor-icons/react";

/**
 * /admin/business-identity
 *
 * The LLC name / EIN / address / BSA officer Stripe needs to underwrite
 * Issuing. Persisted server-side in `db.business_identity` so the Pre-flight
 * dashboard knows whether this gate is green or red and so the auto-generated
 * Cardholder Agreement can pull these values into the template.
 */

const FIELDS = [
  { key: "legal_name", label: "Legal entity name", placeholder: "Zcardon Capital LLC" },
  { key: "dba", label: "DBA (optional)", placeholder: "Zcardon" },
  { key: "ein", label: "EIN (9 digits)", placeholder: "12-3456789", maxLength: 11 },
  { key: "entity_type", label: "Entity type", placeholder: "LLC | Corp | Sole-prop" },
  { key: "state_of_formation", label: "State of formation (2-letter)", placeholder: "DE", maxLength: 2, upper: true },
  { key: "address_line1", label: "Business address — line 1", placeholder: "1 Hacker Way" },
  { key: "address_line2", label: "Address line 2 (optional)", placeholder: "Suite 100" },
  { key: "address_city", label: "City", placeholder: "Menlo Park" },
  { key: "address_state", label: "State (2-letter)", placeholder: "CA", maxLength: 2, upper: true },
  { key: "address_postal", label: "ZIP", placeholder: "94025", maxLength: 12 },
  { key: "bank_account_holder", label: "Business bank account holder", placeholder: "Zcardon Capital LLC" },
  { key: "bsa_officer_name", label: "BSA/AML officer name", placeholder: "Jane Doe" },
  { key: "bsa_officer_email", label: "BSA/AML officer email", placeholder: "compliance@zcardon.com" },
];

export default function BusinessIdentity() {
  const [identity, setIdentity] = useState({});
  const [complete, setComplete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/business-identity");
      setIdentity(r.data.identity || {});
      setComplete(!!r.data.complete);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load identity");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of Object.keys(identity)) {
        const v = (identity[k] || "").trim();
        if (v) payload[k] = v;
      }
      const r = await api.put("/admin/business-identity", payload);
      setIdentity(r.data.identity || {});
      setComplete(!!r.data.complete);
      toast.success(
        r.data.complete
          ? "Saved · ready for Stripe Issuing underwriting"
          : "Saved · some fields still missing for full underwriting"
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setIdentity((p) => ({ ...p, [k]: v }));

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <Link to="/admin/stripe-issuing-preflight" className="inline-flex items-center gap-1 text-[#4A5C50] hover:text-[#112318] text-sm mb-6">
          <ArrowLeft size={16} /> Back to Pre-flight
        </Link>
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h1 className="serif text-4xl text-[#112318]">Business identity</h1>
          {complete && (
            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
              <CheckCircle size={14} weight="fill" /> Complete
            </span>
          )}
        </div>
        <p className="text-[#4A5C50] mt-2 leading-relaxed">
          Stripe Issuing requires your US legal entity name, EIN, and business address for KYB.
          These values also pre-fill the Cardholder Agreement and the Issuing application packet.
          Everything is stored server-side (not in browser localStorage) so it persists across devices.
        </p>

        {loading ? (
          <p className="text-[#4A5C50] mt-8 text-sm">Loading…</p>
        ) : (
          <div className="mt-6 space-y-4" data-testid="business-identity-form">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                  {f.label}
                </label>
                <input
                  value={identity[f.key] || ""}
                  onChange={(e) => set(
                    f.key,
                    f.upper ? e.target.value.toUpperCase().slice(0, f.maxLength || 200)
                            : e.target.value.slice(0, f.maxLength || 200)
                  )}
                  placeholder={f.placeholder}
                  className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                  data-testid={`bi-${f.key}-input`}
                />
              </div>
            ))}

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-[#184A30] hover:bg-[#0F3320] disabled:bg-[#184A30]/50 text-white font-semibold px-5 py-2.5"
                data-testid="bi-save-btn"
              >
                <FloppyDisk size={16} weight="bold" />
                {saving ? "Saving…" : "Save identity"}
              </button>
              <Link to="/admin/stripe-issuing-preflight" className="text-sm text-[#4A5C50] hover:text-[#112318]">
                Cancel
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
